import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building, 
  Users, 
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  FolderOpen,
  Play,
  Settings,
  Save,
  ChevronDown,
  ChevronRight,
  Key,
  Globe,
  Database
} from 'lucide-react';
import { organizationsApi, projectsApi, versionsApi, organizationSettingsApi, evaluationFrameworksApi } from '../services/api';
import { Organization, AUTVersion, OrganizationSettings, EvaluationFramework } from '../types';
import OrganizationSettingsForm from './OrganizationSettingsForm';

interface OrganizationsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}


const Organizations: React.FC<OrganizationsProps> = ({ addNotification }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Project selection state
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [projects, setProjects] = useState<{ id: number; name: string; evaluation_framework_id?: number | null }[]>([]);
  const [versions, setVersions] = useState<AUTVersion[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Settings tab state
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState<OrganizationSettings['settings']>({
    general: {
      default_batch_size: 10,
      max_test_cases_per_generation: 50,
      evaluation_timeout_seconds: 30
    },
    display: {
      dark_mode: false,
      show_notifications: true,
      auto_save: true
    },
    api: {
      openai_api_key: ''
    },
    integrations: {
      webhook_enabled: false,
      database_sync_enabled: true,
      email_notifications_enabled: false
    }
  });
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState<{ id?: number; name: string; description?: string; status?: string; evaluation_framework_id?: number | null } | null>({ name: '' });
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const [evaluationFrameworks, setEvaluationFrameworks] = useState<EvaluationFramework[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    loadOrganizations();
    loadCurrentSelections();
    loadEvaluationFrameworks();
  }, []);

  const loadCurrentSelections = () => {
    // Load current organization selection safely
    const orgIdStr = localStorage.getItem('selectedOrganizationId');
    const parsedOrgId = orgIdStr !== null ? parseInt(orgIdStr, 10) : NaN;
    const orgId = Number.isFinite(parsedOrgId) ? parsedOrgId : null;
    if (orgId !== null) {
      setSelectedOrgId(orgId);
      loadProjectsForOrganization(orgId);
    } else {
      localStorage.removeItem('selectedOrganizationId');
    }

    // Load current project selection safely
    const projectIdStr = localStorage.getItem('selectedProjectId');
    const parsedProjectId = projectIdStr !== null ? parseInt(projectIdStr, 10) : NaN;
    const projId = Number.isFinite(parsedProjectId) ? parsedProjectId : null;
    if (projId !== null && orgId !== null) {
      setSelectedProjectId(projId);
      // Only load versions when both org and project are valid numbers
      loadVersionsForProject(orgId, projId);
    } else {
      localStorage.removeItem('selectedProjectId');
    }

    // Load current version selection safely
    const versionIdStr = localStorage.getItem('selectedVersionId');
    const parsedVersionId = versionIdStr !== null ? parseInt(versionIdStr, 10) : NaN;
    if (Number.isFinite(parsedVersionId)) {
      setSelectedVersionId(parsedVersionId);
    } else {
      localStorage.removeItem('selectedVersionId');
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await organizationsApi.list();
      setOrganizations(data);
    } catch (error) {
      addNotification('Failed to load organizations', 'error');
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvaluationFrameworks = async () => {
    try {
      const data = await evaluationFrameworksApi.getActiveFrameworks();
      setEvaluationFrameworks(data);
    } catch (error) {
      console.error('Error loading evaluation frameworks:', error);
      // Don't show error notification, just log it
    }
  };

  const loadProjectsForOrganization = async (orgId: number) => {
    try {
      setLoadingProjects(true);
      const data = await projectsApi.list(orgId);
      setProjects(data.map(p => ({ id: p.id, name: p.name, evaluation_framework_id: p.evaluation_framework_id })));
    } catch (error) {
      addNotification('Failed to load projects', 'error');
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadVersionsForProject = async (orgId: number, projectId: number) => {
    try {
      setLoadingVersions(true);
      const data = await versionsApi.list(orgId, projectId);
      setVersions(data);
    } catch (error) {
      addNotification('Failed to load versions', 'error');
      console.error('Error loading versions:', error);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      if (!formData.name.trim()) {
        addNotification('Organization name is required', 'error');
        return;
      }

      setSubmitting(true);
      await organizationsApi.create(formData);
      addNotification('Organization created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', status: 'active' });
      loadOrganizations();
    } catch (error) {
      addNotification('Failed to create organization', 'error');
      console.error('Error creating organization:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOrganization = async () => {
    if (!editingOrg) return;

    try {
      if (!formData.name.trim()) {
        addNotification('Organization name is required', 'error');
        return;
      }

      setSubmitting(true);
      await organizationsApi.update(editingOrg.id, formData);
      addNotification('Organization updated successfully', 'success');
      setShowEditModal(false);
      setEditingOrg(null);
      setFormData({ name: '', description: '', status: 'active' });
      loadOrganizations();
    } catch (error) {
      addNotification('Failed to update organization', 'error');
      console.error('Error updating organization:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!deletingOrg) return;

    try {
      setSubmitting(true);
      await organizationsApi.delete(deletingOrg.id);
      addNotification('Organization deleted successfully', 'success');
      setShowDeleteModal(false);
      setDeletingOrg(null);
      loadOrganizations();
    } catch (error) {
      addNotification('Failed to delete organization', 'error');
      console.error('Error deleting organization:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      status: org.status
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (org: Organization) => {
    setDeletingOrg(org);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', status: 'active' });
    setEditingOrg(null);
    setDeletingOrg(null);
  };

  const handleOrganizationChange = (orgId: number) => {
    setSelectedOrgId(orgId);
    localStorage.setItem('selectedOrganizationId', String(orgId));
    loadProjectsForOrganization(orgId);
    
    // Reset project and version selections when organization changes
    setSelectedProjectId(null);
    localStorage.removeItem('selectedProjectId');
    setSelectedVersionId(null);
    localStorage.removeItem('selectedVersionId');
    
    // Reset settings when organization changes
    setOrgSettings(null);
    setActiveSettingsTab('general');
    
    // Dispatch organization change event
    window.dispatchEvent(new CustomEvent('organizationChanged', { 
      detail: { organizationId: orgId } 
    }));
  };

  const handleProjectChange = (projectId: number) => {
    setSelectedProjectId(projectId);
    localStorage.setItem('selectedProjectId', String(projectId));
    setSelectedVersionId(null);
    localStorage.removeItem('selectedVersionId');
    if (selectedOrgId) {
      loadVersionsForProject(selectedOrgId, projectId);
    }
  };

  const handleVersionChange = (versionId: number) => {
    setSelectedVersionId(versionId);
    localStorage.setItem('selectedVersionId', String(versionId));
    
    // Dispatch version change event
    window.dispatchEvent(new CustomEvent('versionChanged', { 
      detail: { versionId: versionId } 
    }));
    
    addNotification(`Project "${versions.find(v => v.id === versionId)?.version_name}" selected`, 'success');
    
    // Load settings for the selected organization
    if (selectedOrgId) {
      loadOrganizationSettings(selectedOrgId);
    }
  };

  const loadOrganizationSettings = async (orgId: number) => {
    try {
      setLoadingSettings(true);
      const settings = await organizationSettingsApi.get(orgId);
      setOrgSettings(settings);
      setSettingsFormData(settings.settings);
    } catch (error) {
      console.error('Failed to load organization settings:', error);
      // If no settings exist, use defaults
      setOrgSettings(null);
    } finally {
      setLoadingSettings(false);
    }
  };

  const saveOrganizationSettings = async () => {
    if (!selectedOrgId) return;
    
    try {
      setSavingSettings(true);
      await organizationSettingsApi.update(selectedOrgId, { settings: settingsFormData });
      addNotification('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save organization settings:', error);
      addNotification('Failed to save organization settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSettingsInputChange = (section: keyof OrganizationSettings['settings'], field: string, value: any) => {
    setSettingsFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'inactive':
        return 'text-slate-300 bg-slate-600';
      case 'suspended':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-slate-300 bg-slate-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'inactive':
        return <XCircle className="h-4 w-4" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <XCircle className="h-4 w-4" />;
    }
  };

  const settingsTabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'display', name: 'Display', icon: Settings },
    { id: 'api', name: 'API Keys', icon: Key },
    { id: 'integrations', name: 'Integrations', icon: Globe }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-200 mb-1">
            Default Batch Size
          </label>
          <input
            type="number"
            value={settingsFormData.general?.default_batch_size || 10}
            onChange={(e) => handleSettingsInputChange('general', 'default_batch_size', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-200 mb-1">
            Max Test Cases per Generation
          </label>
          <input
            type="number"
            value={settingsFormData.general?.max_test_cases_per_generation || 50}
            onChange={(e) => handleSettingsInputChange('general', 'max_test_cases_per_generation', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-200 mb-1">
            Evaluation Timeout (seconds)
          </label>
          <input
            type="number"
            value={settingsFormData.general?.evaluation_timeout_seconds || 30}
            onChange={(e) => handleSettingsInputChange('general', 'evaluation_timeout_seconds', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>
    </div>
  );

  const renderDisplaySettings = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-slate-200">Dark Mode</label>
          <p className="text-sm text-slate-400">Enable dark theme for the application</p>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('display', 'dark_mode', !settingsFormData.display?.dark_mode)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.display?.dark_mode ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.display?.dark_mode ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-slate-200">Show Notifications</label>
          <p className="text-sm text-slate-400">Display system notifications</p>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('display', 'show_notifications', !settingsFormData.display?.show_notifications)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.display?.show_notifications ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.display?.show_notifications ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-slate-200">Auto-save</label>
          <p className="text-sm text-slate-400">Automatically save changes</p>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('display', 'auto_save', !settingsFormData.display?.auto_save)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.display?.auto_save ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.display?.auto_save ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
    </div>
  );

  const renderAPISettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-200 mb-1">
          OpenAI API Key
        </label>
        <div className="flex">
          <input
            type="password"
            value={settingsFormData.api?.openai_api_key || ''}
            onChange={(e) => handleSettingsInputChange('api', 'openai_api_key', e.target.value)}
            placeholder="sk-..."
            className="flex-1 px-3 py-2 border border-slate-600 rounded-l-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
          <button className="px-3 py-2 border border-l-0 border-slate-600 rounded-r-md bg-slate-700 text-slate-300 hover:bg-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500">
            <Key className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsSettings = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border border-slate-700 rounded-lg">
        <div className="flex items-center space-x-3">
          <Globe className="h-5 w-5 text-blue-500" />
          <div>
            <h4 className="text-sm font-medium text-slate-100">Webhook Integration</h4>
            <p className="text-sm text-slate-400">Send test results to external systems</p>
          </div>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('integrations', 'webhook_enabled', !settingsFormData.integrations?.webhook_enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.integrations?.webhook_enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.integrations?.webhook_enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      
      <div className="flex items-center justify-between p-4 border border-slate-700 rounded-lg">
        <div className="flex items-center space-x-3">
          <Database className="h-5 w-5 text-green-500" />
          <div>
            <h4 className="text-sm font-medium text-slate-100">Database Sync</h4>
            <p className="text-sm text-slate-400">Synchronize with external databases</p>
          </div>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('integrations', 'database_sync_enabled', !settingsFormData.integrations?.database_sync_enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.integrations?.database_sync_enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.integrations?.database_sync_enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      
      <div className="flex items-center justify-between p-4 border border-slate-700 rounded-lg">
        <div className="flex items-center space-x-3">
          <Globe className="h-5 w-5 text-purple-500" />
          <div>
            <h4 className="text-sm font-medium text-slate-100">Email Notifications</h4>
            <p className="text-sm text-slate-400">Send email alerts for test results</p>
          </div>
        </div>
        <button 
          onClick={() => handleSettingsInputChange('integrations', 'email_notifications_enabled', !settingsFormData.integrations?.email_notifications_enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            settingsFormData.integrations?.email_notifications_enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-slate-600 transition-transform ${
            settingsFormData.integrations?.email_notifications_enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
    </div>
  );

  const renderSettingsTabContent = () => {
    switch (activeSettingsTab) {
      case 'general':
        return renderGeneralSettings();
      case 'display':
        return renderDisplaySettings();
      case 'api':
        return renderAPISettings();
      case 'integrations':
        return renderIntegrationsSettings();
      default:
        return renderGeneralSettings();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Organizations</h1>
            <p className="text-slate-300 mt-2">Select organization and project to configure settings</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Organization
          </button>
        </div>
      </div>

      {/* Project Selection */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FolderOpen className="h-6 w-6 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-100">Project Selection</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowManageModal(true)}
              className="inline-flex items-center px-3 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Organizations
            </button>
            <button
              onClick={() => setShowProjectModal(true)}
              disabled={!selectedOrgId}
              className="inline-flex items-center px-3 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add/Edit Project
            </button>
          </div>
        </div>
        <p className="text-slate-300 mb-6">Select an organization and project to use across all screens</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Organization Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Organization
            </label>
            <select
              value={selectedOrgId || ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  // Clear selections and avoid API calls
                  setSelectedOrgId(null);
                  localStorage.removeItem('selectedOrganizationId');
                  setProjects([]);
                  setSelectedProjectId(null);
                  localStorage.removeItem('selectedProjectId');
                  setVersions([]);
                  setSelectedVersionId(null);
                  localStorage.removeItem('selectedVersionId');
                  return;
                }
                handleOrganizationChange(parseInt(val));
              }}
              className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select an organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Project
            </label>
            <div className="relative">
              <select
                value={selectedProjectId || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setSelectedProjectId(null);
                    localStorage.removeItem('selectedProjectId');
                    setVersions([]);
                    setSelectedVersionId(null);
                    localStorage.removeItem('selectedVersionId');
                    return;
                  }
                  handleProjectChange(parseInt(val));
                }}
                disabled={!selectedOrgId || loadingProjects}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 disabled:bg-slate-600 disabled:cursor-not-allowed"
              >
                <option value="">
                  {loadingProjects ? 'Loading projects...' : 'Select a project'}
                </option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {loadingProjects && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          </div>

          {/* Version Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Version
            </label>
            <div className="relative">
              <select
                value={selectedVersionId || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setSelectedVersionId(null);
                    localStorage.removeItem('selectedVersionId');
                    return;
                  }
                  handleVersionChange(parseInt(val));
                }}
                disabled={!selectedOrgId || !selectedProjectId || loadingVersions}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 disabled:bg-slate-600 disabled:cursor-not-allowed"
              >
                <option value="">
                  {loadingVersions ? 'Loading versions...' : 'Select a version'}
                </option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.version_name} {version.is_active ? '(Active)' : ''}
                  </option>
                ))}
              </select>
              {loadingVersions && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Current Selection Display */}
        {selectedOrgId && selectedProjectId && selectedVersionId && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Active Project: <span className="font-semibold">
                    {versions.find(v => v.id === selectedVersionId)?.version_name}
                  </span>
                </p>
                <p className="text-xs text-green-600">
                  Organization: {organizations.find(o => o.id === selectedOrgId)?.name}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Organization Settings */}
      {selectedOrgId && selectedProjectId && selectedVersionId && (
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-100">Organization Settings</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Configure settings for <span className="font-medium">{organizations.find(o => o.id === selectedOrgId)?.name}</span>
                </p>
              </div>
              <button
                onClick={() => selectedOrgId && loadOrganizationSettings(selectedOrgId)}
                disabled={loadingSettings}
                className="px-3 py-1 text-sm font-medium text-slate-200 bg-white border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loadingSettings ? 'Loading...' : 'Load Settings'}
              </button>
            </div>
          </div>

          {/* Settings Tabs */}
          <div className="px-6">
            <div className="border-b border-slate-700">
              <nav className="-mb-px flex space-x-8">
                {settingsTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSettingsTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeSettingsTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="py-6">
              {renderSettingsTabContent()}
            </div>

            {/* Save Button */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 pb-6">
              <button
                onClick={saveOrganizationSettings}
                disabled={savingSettings}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Management Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-slate-100">{editingProject ? 'Edit Project' : 'Create Project'}</h3>
                <button
                  onClick={() => { setShowProjectModal(false); setEditingProject(null); }}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Project Name *</label>
                  <input
                    type="text"
                    value={editingProject?.name || ''}
                    onChange={(e) => setEditingProject(prev => ({ ...(prev || { name: '' }), name: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Web App, Mobile App"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Description</label>
                  <textarea
                    value={editingProject?.description || ''}
                    onChange={(e) => setEditingProject(prev => ({ ...(prev || { name: '' }), description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Brief description..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Evaluation Framework</label>
                  <select
                    value={editingProject?.evaluation_framework_id || ''}
                    onChange={(e) => setEditingProject(prev => ({ 
                      ...(prev || { name: '' }), 
                      evaluation_framework_id: e.target.value ? parseInt(e.target.value) : null 
                    }))}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">No Framework (Optional)</option>
                    {evaluationFrameworks.map(framework => (
                      <option key={framework.id} value={framework.id}>
                        {framework.name} ({framework.framework_type})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-400">
                    Select the evaluation framework for this project to determine which metrics are used for test evaluations
                  </p>
                  {evaluationFrameworks.length === 0 && (
                    <p className="mt-1 text-xs text-yellow-600">
                      No active evaluation frameworks found. Create frameworks in Test Context &gt; Evaluation Frameworks.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setShowProjectModal(false); setEditingProject(null); }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-white border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selectedOrgId) return;
                    if (!editingProject?.name?.trim()) { addNotification('Project name is required', 'error'); return; }
                    try {
                      setProjectSubmitting(true);
                      if (editingProject && editingProject.id) {
                        await projectsApi.update(selectedOrgId, editingProject.id, { 
                          name: editingProject.name, 
                          description: editingProject.description,
                          evaluation_framework_id: editingProject.evaluation_framework_id ?? undefined 
                        });
                        addNotification('Project updated successfully', 'success');
                      } else {
                        await projectsApi.create(selectedOrgId, { 
                          name: editingProject?.name || '', 
                          description: editingProject?.description || '', 
                          status: 'active',
                          evaluation_framework_id: editingProject?.evaluation_framework_id ?? undefined 
                        } as any);
                        addNotification('Project created successfully', 'success');
                      }
                      await loadProjectsForOrganization(selectedOrgId);
                      setShowProjectModal(false);
                      setEditingProject(null);
                    } catch (error: any) {
                      const errorMessage = error.response?.data?.detail || 'Failed to save project';
                      addNotification(errorMessage, 'error');
                      console.error('Error saving project:', error);
                    } finally {
                      setProjectSubmitting(false);
                    }
                  }}
                  disabled={projectSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {projectSubmitting ? 'Saving...' : (editingProject?.id ? 'Update Project' : 'Create Project')}
                </button>
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-100 mb-2">Existing Projects</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {projects.length === 0 ? (
                    <p className="text-sm text-slate-400">No projects yet.</p>
                  ) : (
                    projects.map(p => (
                      <div key={p.id} className="flex flex-col p-3 border border-gray-200 rounded hover:bg-slate-700">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-800">{p.name}</span>
                          <div className="space-x-2">
                            <button
                              onClick={() => setEditingProject({ 
                                id: p.id, 
                                name: p.name, 
                                evaluation_framework_id: p.evaluation_framework_id 
                              })}
                              className="px-2 py-1 text-xs border border-slate-600 rounded hover:bg-slate-700"
                            >Edit</button>
                          <button
                            onClick={async () => {
                              if (!selectedOrgId) return;
                              try {
                                await projectsApi.delete(selectedOrgId, p.id);
                                addNotification('Project deleted', 'success');
                                await loadProjectsForOrganization(selectedOrgId);
                                if (selectedProjectId === p.id) {
                                  setSelectedProjectId(null);
                                  localStorage.removeItem('selectedProjectId');
                                  setVersions([]);
                                  setSelectedVersionId(null);
                                  localStorage.removeItem('selectedVersionId');
                                }
                              } catch (error) {
                                addNotification('Failed to delete project', 'error');
                                console.error('Error deleting project:', error);
                              }
                            }}
                            className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                          >Delete</button>
                          </div>
                        </div>
                        {p.evaluation_framework_id && (
                          <div className="text-xs text-slate-400 flex items-center mt-1">
                            <Database className="h-3 w-3 mr-1" />
                            Framework: {evaluationFrameworks.find(f => f.id === p.evaluation_framework_id)?.name || `ID: ${p.evaluation_framework_id}`}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Organization</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Acme Corp, Client ABC"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Brief description of the organization..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-white border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOrganization}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && editingOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-slate-100">Edit Organization: {editingOrg.name}</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Basic Info Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-200 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="e.g., Acme Corp, Client ABC"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-200 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Brief description of the organization..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-200 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-slate-200 bg-white border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditOrganization}
                      disabled={submitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Updating...' : 'Update Organization'}
                    </button>
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-slate-100 mb-2">Delete Organization</h3>
                <p className="text-sm text-slate-300 mb-4">
                  Are you sure you want to delete <strong>{deletingOrg.name}</strong>? 
                  This action cannot be undone and will remove all associated data.
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-white border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrganization}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Deleting...' : 'Delete Organization'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Organizations Modal */}
      {showManageModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-slate-100">Manage Organizations</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Organization
                  </button>
                  <button
                    onClick={() => setShowManageModal(false)}
                    className="text-slate-400 hover:text-slate-200"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {organizations.length === 0 ? (
                <div className="text-center py-12">
                  <Building className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-slate-100 mb-2">No organizations yet</h3>
                  <p className="text-slate-300 mb-6">Get started by creating your first organization</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Organization
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {organizations.map((org) => (
                    <div key={org.id} className="border border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Building className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-100">{org.name}</h4>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(org.status)}`}>
                              {getStatusIcon(org.status)}
                              <span className="ml-1 capitalize">{org.status}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setShowManageModal(false);
                              openEditModal(org);
                            }}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            title="Edit organization"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setShowManageModal(false);
                              openDeleteModal(org);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete organization"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {org.description && (
                        <p className="text-xs text-slate-300 mt-2 line-clamp-2">{org.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
